import { Modal, TextInput, Button, Stack, Group } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { departmentSchema, type DepartmentFormValues } from '../schemas/departmentSchema';
import type { Department } from '@/types/models';

interface DepartmentFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: DepartmentFormValues, keepOpen?: boolean) => void;
  department?: Department | null;
  isLoading?: boolean;
  /** When true, hides "Create & Add Another" button (for inline use in asset form) */
  inlineMode?: boolean;
}

export function DepartmentForm({
  opened,
  onClose,
  onSubmit,
  department,
  isLoading,
  inlineMode = false,
}: DepartmentFormProps) {
  const form = useForm<DepartmentFormValues>({
    validate: zodResolver(departmentSchema),
    initialValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (department) {
      form.setValues({
        name: department.name,
      });
    } else {
      form.reset();
    }
  }, [department, opened]);

  const handleSubmit = (values: DepartmentFormValues, keepOpen = false) => {
    onSubmit(values, keepOpen);
    if (keepOpen) {
      form.reset();
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={department ? 'Edit Department' : 'Create Department'}
      size="md"
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={(e) => {
          // Allow Enter to submit form from any field (except textareas)
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
            form.onSubmit(handleSubmit)();
          }
        }}
      >
        <Stack gap="md">
          <TextInput
            label="Department Name"
            placeholder="e.g., Engineering, Sales, HR"
            required
            {...form.getInputProps('name')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            {!department && !inlineMode && (
              <Button
                type="button"
                variant="light"
                onClick={() => form.onSubmit((values) => handleSubmit(values, true))()}
                loading={isLoading}
              >
                Create & Add Another
              </Button>
            )}
            <Button
              type="button"
              onClick={() => form.onSubmit((values) => handleSubmit(values, false))()}
              loading={isLoading}
            >
              {department ? 'Update Department' : 'Create Department'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
